import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { useRequestData, useLabels } from 'wsm-common-data';
import useFacetFilter from '../hooks/useFacetFilter';
import FacetOption from './FacetOption';
import { facetAccordionEvent, facetSelectionEvent } from '../utils/wueTracking';
import OFFER_TYPE_LABEL_KEYS from '../utils/incentiveTypeMap';

export const FACET_HEADINGS = {
	offerTypes: 'Offer Types',
	year: 'Year',
	bodyStyle: 'Body Style',
	make: 'Make',
	model: 'Model'
};

const toDisplayLabel = (facetKey, value, labels) => {
	if (facetKey === 'offerTypes') {
		const labelKey = OFFER_TYPE_LABEL_KEYS[value];
		if (labelKey) {
			const label = labels.get(labelKey);
			if (label !== labelKey) return label;
		}
	}
	if (value.length <= 3) return value.toUpperCase();
	return value.charAt(0).toUpperCase() + value.slice(1);
};

const FacetGroup = ({ facetKey }) => {
	const { availableOptions, selectedValues, handleToggle } =
		useFacetFilter(facetKey);
	const [collapsed, setCollapsed] = useState(false);
	const headingId = `facet-group-heading-${facetKey}`;

	const { widgetName, windowId } = useRequestData();
	const labels = useLabels();

	const handleHeadingClick = () => {
		const nextCollapsed = !collapsed;
		setCollapsed(nextCollapsed);
		trackEvent(
			widgetName,
			windowId,
			facetAccordionEvent({
				heading: FACET_HEADINGS[facetKey],
				expanded: !nextCollapsed
			})
		);
	};

	const trackedToggle = useCallback(
		(value) => {
			const wasSelected = selectedValues.includes(value);
			handleToggle(value);
			trackEvent(
				widgetName,
				windowId,
				facetSelectionEvent({
					heading: FACET_HEADINGS[facetKey],
					value,
					applied: !wasSelected
				})
			);
		},
		[selectedValues, handleToggle, widgetName, windowId, facetKey]
	);

	if (availableOptions.length === 0) {
		return null;
	}

	return (
		<div className="facet-group">
			{/* AC-4: h3 provides semantic heading for assistive technology heading navigation.
			    The <button> inside handles collapse interaction; id stays on button for aria-labelledby on the options list. */}
			<h3 className="m-0">
				<button
					type="button"
					className="facet-group__heading"
					id={headingId}
					onClick={handleHeadingClick}
					aria-expanded={!collapsed}
				>
					<span className="font-weight-bold">
						{FACET_HEADINGS[facetKey]}
					</span>
					<span className="facet-group__toggle" aria-hidden="true">
						{collapsed ? '⊕' : '⊖'}
					</span>
				</button>
			</h3>
			{!collapsed && (
				<ul
					role="group"
					className="facet-group__options"
					aria-labelledby={headingId}
				>
					{availableOptions.map(({ value, count }) => (
						<FacetOption
							key={value}
							value={value}
							label={toDisplayLabel(facetKey, value, labels)}
							count={count}
							checked={selectedValues.includes(value)}
							onChange={trackedToggle}
						/>
					))}
				</ul>
			)}
		</div>
	);
};

FacetGroup.propTypes = {
	facetKey: PropTypes.string.isRequired
};

export default FacetGroup;
